/**
 * Invite Organization User Modal
 *
 * Modal for inviting a new user to an organization.
 * Calls adminInviteUser cloud function which:
 * - Creates an org_users record with pending status
 * - Sends invitation email with accept link
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Mail, UserPlus } from 'lucide-react';

interface InviteOrgUserModalProps {
  orgId: string;
  orgName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

type OrgUserRole = 'owner' | 'admin' | 'member';

export function InviteOrgUserModal({
  orgId,
  orgName,
  open,
  onOpenChange,
  onSuccess,
}: InviteOrgUserModalProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    role: 'member' as OrgUserRole,
    firstName: '',
    lastName: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      // Call cloud function to create invitation and send email
      const adminInviteUser = httpsCallable(functions, 'adminInviteUser');
      const result = await adminInviteUser({
        orgId,
        email: formData.email,
        role: formData.role,
        firstName: formData.firstName,
        lastName: formData.lastName,
      });

      const data = result.data as { success: boolean; message: string };

      toast({
        title: 'Invitation Sent',
        description: data.message || `An invitation has been sent to ${formData.email}`,
      });

      // Reset form
      setFormData({
        email: '',
        role: 'member',
        firstName: '',
        lastName: '',
      });
      setErrors({});

      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      console.error('Failed to invite user:', error);

      // Parse Firebase function error
      const errorMessage = error.message?.includes('already-exists')
        ? 'This user is already a member of the organization'
        : error.message || 'An unexpected error occurred';

      toast({
        title: 'Failed to Send Invitation',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        email: '',
        role: 'member',
        firstName: '',
        lastName: '',
      });
      setErrors({});
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[hsl(220,22%,12%)] border-[hsl(220,15%,20%)] text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-[hsl(220,70%,50%)]" />
            Invite User to {orgName || 'Organization'}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Send an invitation email to add a new user to this organization.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName" className="text-gray-300">
                First Name
              </Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, firstName: e.target.value }))
                }
                placeholder="John"
                className="bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white placeholder:text-gray-500"
              />
              {errors.firstName && (
                <p className="text-xs text-red-400">{errors.firstName}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName" className="text-gray-300">
                Last Name
              </Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, lastName: e.target.value }))
                }
                placeholder="Smith"
                className="bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white placeholder:text-gray-500"
              />
              {errors.lastName && (
                <p className="text-xs text-red-400">{errors.lastName}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-300">
              Email Address
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, email: e.target.value }))
                }
                placeholder="john@example.com"
                className="pl-10 bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white placeholder:text-gray-500"
              />
            </div>
            {errors.email && (
              <p className="text-xs text-red-400">{errors.email}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="role" className="text-gray-300">
              Role
            </Label>
            <Select
              value={formData.role}
              onValueChange={(v) =>
                setFormData((p) => ({ ...p, role: v as OrgUserRole }))
              }
            >
              <SelectTrigger className="bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(220,22%,12%)] border-[hsl(220,15%,25%)]">
                <SelectItem value="member">
                  <div className="flex flex-col">
                    <span>Member</span>
                    <span className="text-xs text-gray-500">
                      Can view and run checks
                    </span>
                  </div>
                </SelectItem>
                <SelectItem value="admin">
                  <div className="flex flex-col">
                    <span>Admin</span>
                    <span className="text-xs text-gray-500">
                      Can manage jobs and invite users
                    </span>
                  </div>
                </SelectItem>
                <SelectItem value="owner">
                  <div className="flex flex-col">
                    <span>Owner</span>
                    <span className="text-xs text-gray-500">
                      Full access including billing
                    </span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="bg-transparent border-[hsl(220,15%,25%)] text-gray-400 hover:bg-[hsl(220,15%,15%)] hover:text-white"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[hsl(220,70%,50%)] hover:bg-[hsl(220,70%,45%)] text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send Invitation
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
